import React, { useEffect, useState } from "react";
import { StatCard, StatCardSkeleton, DashboardStat } from "./StatCard";
import { RevenueFilter } from "./RevenueFilter";
import { API_ROUTES } from "@/services/environment";
import { GET } from "@/services/api";
import { useAppSelector } from "@/hooks/hook";

const dashboardStats: DashboardStat[] = [
    {
        label: "Revenue",
        value: "$0.00",
        info: "Calculated and updated once the event is completed.",
    },
    {
        label: "Events",
        value: "0",
        info: "Counted based on the actual event month or year.",
    },
    {
        label: "Total Points",
        value: "0",
        info: "Added once the booking is confirmed and the platform fee is received.",
    },
];

export const StatsOverview = () => {
    const { getEventsCountApi, getRewardPointsEarnedApi } = API_ROUTES;
    const { refreshToken, business_id } = useAppSelector((store) => store.user);
    const { currentBusiness } = useAppSelector((store) => store.business);
    const effectiveBusinessId = currentBusiness ? currentBusiness : business_id;

    const [selectedMonth, setSelectedMonth] = useState<number | string>(
        new Date().getMonth()
    );
    const [selectedYear, setSelectedYear] = useState<number | string>(
        new Date().getFullYear()
    );

    const [revenue, setRevenue] = useState<string>();
    const [isRevenueLoading, setIsRevenueLoading] = useState<boolean>(true);
    const [eventsCount, setEventsCount] = useState<string>();
    const [isEventsLoading, setIsEventsLoading] = useState<boolean>(true);
    const [totalPoints, setTotalPoints] = useState<string>();
    const [isPointsLoading, setIsPointsLoading] = useState<boolean>(true);

    useEffect(() => {
        if (!effectiveBusinessId) return;
        (async () => {
            try {
                setIsEventsLoading(true);
                const res = await GET(
                    `${getEventsCountApi}?business_id=${effectiveBusinessId}&month=${selectedMonth}&year=${selectedYear}`,
                    refreshToken
                );
                setEventsCount(String(res?.data?.data?.count ?? 0));
            } catch (error) {
                console.log("get events count error:", error);
            } finally {
                setIsEventsLoading(false);
            }
        })();
    }, [effectiveBusinessId, selectedMonth, selectedYear]);

    useEffect(() => {
        if (!effectiveBusinessId) return;
        (async () => {
            try {
                setIsPointsLoading(true);
                const res = await GET(
                    `${getRewardPointsEarnedApi}/${effectiveBusinessId}?month=${selectedMonth}&year=${selectedYear}`,
                    refreshToken
                );
                setTotalPoints(String(res?.data?.record?.earned_points ?? 0));
            } catch (error) {
                console.log("get total points error:", error);
            } finally {
                setIsPointsLoading(false);
            }
        })();
    }, [effectiveBusinessId, selectedMonth, selectedYear]);

    const isLoading = isRevenueLoading || isEventsLoading || isPointsLoading;

    const stats = dashboardStats.map((stat) => {
        if (stat.label === "Revenue" && revenue) return { ...stat, value: revenue };
        if (stat.label === "Events" && eventsCount !== undefined) return { ...stat, value: eventsCount };
        if (stat.label === "Total Points" && totalPoints !== undefined) return { ...stat, value: totalPoints };
        return stat;
    });

    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between rounded-2xl p-5 bg-white">
            <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-1 sm:gap-3 sm:flex-wrap">
                {isLoading
                    ? dashboardStats.map((stat) => <StatCardSkeleton key={stat.label} />)
                    : stats.map((stat) => <StatCard key={stat.label} stat={stat} />)}
            </div>
            <RevenueFilter
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                onMonthChange={setSelectedMonth}
                onYearChange={setSelectedYear}
                onRevenueChange={setRevenue}
                onLoadingChange={setIsRevenueLoading}
            />
        </div>
    );
};
